import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cachedGet } from "@/lib/cache";
import { StatusPill, ScorePill } from "@/components/Shared";
import { DemoBadge } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, ClipboardList, CheckCircle2, GraduationCap, HardHat, Search, CalendarDays, AlarmClock, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";

const FILTERS = [
  { id: "new_applications", label: "New Applications" },
  { id: "assessment_complete", label: "Assessment Complete" },
  { id: "assessment_day", label: "Assessment Day" },
  { id: "training_academy", label: "Training Academy" },
  { id: "declined", label: "Declined" },
];

const HEADERS = [
  { label: "Candidate", sortKey: "name" },
  { label: "Postcode" },
  { label: "Local Authority" },
  { label: "Referral" },
  { label: "Theory", sortKey: "theory" },
  { label: "Interview" },
  { label: "Simulator" },
  { label: "Day Score", sortKey: "day_score" },
  { label: "Funding" },
  { label: "Status" },
];

function StatCard({ icon: Icon, label, value, testid, accent }) {
  return (
    <div className="bg-slate-panel border border-steel rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${accent ? "text-lomond" : "text-slate-500"}`} />
      </div>
      <p className="font-heading font-black text-charcoal text-3xl">{value ?? "—"}</p>
    </div>
  );
}

function TableSkeleton() {
  return (
    <tbody data-testid="candidates-table-loading">
      {Array.from({ length: 8 }).map((_, r) => (
        <tr key={r} className="border-b border-steel">
          {Array.from({ length: 10 }).map((_, c) => (
            <td key={c} className="px-4 py-3">
              <div className="h-4 bg-steel/30 rounded-sm animate-pulse" style={{ width: c === 0 ? "70%" : "45%" }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export default function StaffDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [calSummary, setCalSummary] = useState(null);
  const [candData, setCandData] = useState(null);
  const [candLoading, setCandLoading] = useState(true);
  const [candError, setCandError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [devReminders, setDevReminders] = useState([]);
  const [reviewReady, setReviewReady] = useState([]);
  const [filter, setFilter] = useState("new_applications");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState("pipeline");
  const [sortDir, setSortDir] = useState("");

  useEffect(() => {
    cachedGet("/staff/overview", 45000).then(setOverview).catch(() => {});
    cachedGet("/staff/calendar-summary", 45000).then(setCalSummary).catch(() => {});
    api.get("/staff/development/reminders").then((r) => setDevReminders(r.data)).catch(() => {});
    api.get("/staff/development/review-ready").then((r) => setReviewReady(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQuery(query); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setCandLoading(true);
    setCandError(false);
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize), filter, sort });
    if (debouncedQuery.trim()) params.set("search", debouncedQuery.trim());
    if (sortDir) params.set("sort_dir", sortDir);
    cachedGet(`/staff/candidates?${params.toString()}`, 30000)
      .then((d) => { if (!cancelled) { setCandData(d); setCandLoading(false); } })
      .catch(() => { if (!cancelled) { setCandError(true); setCandLoading(false); } });
    return () => { cancelled = true; };
  }, [page, pageSize, filter, debouncedQuery, sort, sortDir, reloadTick]);

  const toggleSort = (key) => {
    const primaryDir = key === "name" ? "asc" : "desc";
    if (sort !== key) { setSort(key); setSortDir(primaryDir); }
    else if (sortDir === primaryDir) { setSortDir(primaryDir === "asc" ? "desc" : "asc"); }
    else { setSort("pipeline"); setSortDir(""); }
    setPage(1);
  };

  const items = candData?.items || [];

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Management</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="staff-dashboard-heading">
          Applicant Pipeline
        </h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="overview-cards">
        <StatCard icon={Users} label="Applicants" value={overview?.applicants} testid="stat-applicants" accent />
        <StatCard icon={ClipboardList} label="Awaiting Assessment" value={overview?.awaiting_assessment} testid="stat-awaiting" />
        <StatCard icon={CheckCircle2} label="Assessment Done" value={overview?.assessment_completed} testid="stat-completed" />
        <StatCard icon={CalendarDays} label="Invited to Day" value={overview?.invited} testid="stat-invited" />
        <StatCard icon={GraduationCap} label="Trainees" value={overview?.trainees} testid="stat-trainees" />
        <StatCard icon={HardHat} label="Operators" value={overview?.operators} testid="stat-operators" />
      </div>

      {devReminders.length > 0 && (
        <section className="bg-amber-50 border border-amber-300 rounded-md p-5" data-testid="development-reminders-card">          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-3 flex items-center gap-2">
            <AlarmClock className="h-3.5 w-3.5" /> Development review reminders
          </p>
          <div className="space-y-2">
            {devReminders.map((o) => (
              <button key={o.id} onClick={() => navigate(`/staff/operators/${o.id}`)}
                className="w-full flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-sm px-3.5 py-2.5 text-left hover:border-amber-400 transition-colors"
                data-testid={`reminder-${o.id}`}>
                <span className="text-sm font-semibold text-charcoal shrink-0">{o.name} <span className="text-slate-400 font-normal">— week {o.weeks_employed}</span></span>
                <span className="text-xs text-amber-800 text-right">{o.reminders.map((r) => r.message).join(" · ")}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {reviewReady.length > 0 && (
        <section className="bg-white border-2 border-lomond rounded-md p-5" data-testid="review-ready-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-3 flex items-center gap-2">
            <HardHat className="h-3.5 w-3.5" /> Operators ready for competence review
          </p>
          <div className="space-y-2">
            {reviewReady.map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-3 bg-elevated border border-steel rounded-sm px-3.5 py-2.5 flex-wrap" data-testid={`review-ready-${o.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-charcoal">{o.name}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                    {o.grade} — {o.seat_hours}h — AP3 {o.ap3_status}{o.ap3_outcome ? ` (${o.ap3_outcome.replace(/_/g, " ")})` : ""} — feedback {o.feedback_avg ?? "—"}/5 — review due {o.review_due || "—"}
                  </p>
                </div>
                <button onClick={() => navigate(`/staff/operators/${o.id}/review`)}
                  className="px-3.5 py-2 rounded-md bg-lomond text-black text-xs font-bold uppercase tracking-wide hover:bg-[#E6B400] shrink-0"
                  data-testid={`start-review-${o.id}`}>
                  {o.review_in_progress ? "Resume Review" : "Start Review"}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {calSummary && (
        <section className="grid lg:grid-cols-2 gap-4" data-testid="calendar-widget">
          <div className="bg-charcoal text-white rounded-md p-5" data-testid="next-assessment-day">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2 flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5" /> Next assessment day
            </p>
            {calSummary.next ? (
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-heading font-extrabold uppercase text-xl" data-testid="next-event-date">
                    {new Date(calSummary.next.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                  </p>
                  <p className="text-sm text-white/70 mt-0.5">{calSummary.next.start_time} — {calSummary.next.location_name}</p>
                  <p className="font-mono text-xs text-lomond mt-1" data-testid="next-event-bookings">
                    {calSummary.next.booked} / {calSummary.next.capacity} booked
                    {calSummary.next.waiting > 0 ? ` — ${calSummary.next.waiting} waiting` : ""}
                  </p>
                </div>
                <Button size="sm" onClick={() => navigate(`/staff/calendar/${calSummary.next.id}`)}
                  className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="view-next-event-btn">
                  View Assessment Day
                </Button>
              </div>
            ) : (
              <p className="text-sm text-white/60" data-testid="no-next-event">No upcoming assessment days. <button className="text-lomond underline" onClick={() => navigate("/staff/calendar")}>Schedule one</button></p>
            )}
          </div>
          <div className="bg-white border border-steel rounded-md p-5" data-testid="upcoming-30-days">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Upcoming assessments — next 30 days</p>
            <div className="space-y-2">
              {(calSummary.upcoming || []).slice(0, 4).map((e) => (
                <button key={e.id} onClick={() => navigate(`/staff/calendar/${e.id}`)}
                  className="w-full flex items-center justify-between gap-3 bg-elevated hover:bg-lomond/20 border border-steel rounded-sm px-3 py-2 transition-colors text-left"
                  data-testid={`upcoming-event-${e.id}`}>
                  <span className="text-sm font-semibold text-charcoal">
                    {new Date(e.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} — {e.start_time}
                  </span>
                  <span className="font-mono text-xs text-slate-500">{e.booked}/{e.capacity} booked</span>
                </button>
              ))}
              {!(calSummary.upcoming || []).length && <p className="text-sm text-slate-500">Nothing scheduled in the next 30 days.</p>}
            </div>
          </div>
        </section>
      )}

      {overview?.average_theory_score != null && (
        <p className="text-sm text-slate-500" data-testid="avg-theory">
          Average online assessment score: <span className="font-mono font-bold text-lomond">{overview.average_theory_score}%</span>
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex gap-2 flex-wrap" data-testid="filter-tabs">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }}
              className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest border transition-colors ${
                filter === f.id ? "bg-lomond text-black border-lomond font-bold" : "border-steel text-slate-500 hover:border-lomond hover:text-lomond"
              }`}
              data-testid={`filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name / postcode"
            className="bg-slate-panel border-steel text-charcoal pl-9 h-9 w-full sm:w-64" data-testid="candidate-search" />
        </div>
      </div>

      <div className="bg-slate-panel border border-steel rounded-sm overflow-x-auto" data-testid="candidates-table-wrapper">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="border-b border-steel text-left">
              {HEADERS.map((h) => (
                <th key={h.label} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {h.sortKey ? (
                    <button onClick={() => toggleSort(h.sortKey)} className="flex items-center gap-1 uppercase tracking-widest hover:text-lomond transition-colors" data-testid={`sort-${h.sortKey}`}>
                      {h.label}
                      {sort === h.sortKey && (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </button>
                  ) : h.label}
                </th>
              ))}
            </tr>
          </thead>
          {candLoading && !candData ? <TableSkeleton /> : (
            <tbody>
              {items.map((c) => (
                <tr key={c.id}
                  onClick={() => navigate(`/staff/candidates/${c.id}`)}
                  className="border-b border-steel hover:bg-elevated cursor-pointer transition-colors"
                  data-testid={`candidate-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-charcoal">{c.name}</span>
                      {c.is_demo && <DemoBadge />}
                      {c.management_review_required && <span className="h-2 w-2 rounded-full bg-red-500 inline-block" title="Management review required" data-testid={`review-flag-${c.id}`} />}
                    </div>
                    <span className="text-xs text-slate-500">{c.email}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{c.postcode || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.local_authority || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{c.referral_source || "—"}</td>
                  <td className="px-4 py-3"><ScorePill value={c.theory_score} suffix="%" testid={`theory-${c.id}`} /></td>
                  <td className="px-4 py-3"><ScorePill value={c.interview_score ? Math.round(c.interview_score / 5 * 100) : null} suffix="%" testid={`int-${c.id}`} /></td>
                  <td className="px-4 py-3"><ScorePill value={c.simulator_score ? Math.round(c.simulator_score / 5 * 100) : null} suffix="%" testid={`sim-${c.id}`} /></td>
                  <td className="px-4 py-3"><ScorePill value={c.day_score} suffix="%" testid={`overall-${c.id}`} /></td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-mono ${c.funding_potential === "Potential" ? "text-emerald-300" : "text-slate-500"}`} data-testid={`funding-${c.id}`}>
                      {c.funding_potential}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusPill stage={c.role === "applicant" ? c.stage : c.role} label={c.role === "applicant" ? c.stage_label : c.role.toUpperCase()} /></td>
                </tr>
              ))}
              {!items.length && !candError && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500 text-sm" data-testid="no-candidates">
                  No candidates match this filter.
                </td></tr>
              )}
              {candError && (
                <tr><td colSpan={10} className="px-4 py-10 text-center" data-testid="candidates-error">
                  <AlertTriangle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 mb-3">Could not load candidates. Check your connection and try again.</p>
                  <Button size="sm" onClick={() => setReloadTick((t) => t + 1)} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="candidates-retry-btn">Retry</Button>
                </td></tr>
              )}
            </tbody>
          )}
        </table>
        {candData && !candError && (
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-steel bg-white" data-testid="candidates-pagination">
            <p className="text-xs font-mono text-slate-500" data-testid="page-info">
              {candData.total === 0 ? "No candidates" : `Showing ${(candData.page - 1) * candData.page_size + 1}–${Math.min(candData.page * candData.page_size, candData.total)} of ${candData.total}`}
            </p>
            <div className="flex items-center gap-2">
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="h-8 rounded-sm border border-steel bg-white px-2 text-xs font-mono text-charcoal" data-testid="page-size-select">
                {[25, 50, 100].map((n) => <option key={n} value={n}>{`${n} / page`}</option>)}
              </select>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="border-steel text-charcoal bg-white" data-testid="page-prev">Prev</Button>
              <span className="text-xs font-mono text-slate-500" data-testid="page-numbers">{candData.page} / {candData.pages}</span>
              <Button variant="outline" size="sm" disabled={page >= candData.pages} onClick={() => setPage((p) => p + 1)}
                className="border-steel text-charcoal bg-white" data-testid="page-next">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
